import React, { useState, useEffect, useRef, useCallback } from "react";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Printer, Download, FileText, X, Loader2, Edit } from "lucide-react";
import { getStudentById, getClassById, getSectionById, getSubjectById, getTeacherById } from "@/services/dataService";
import { getClassesDB, getSectionsDB, getSubjectsDB, getTeachersDB, getStudentsDB } from "@/services/databaseService";
import { loadAmiriFont, ARABIC_FONT_NAME } from "@/utils/fontLoader";
import { toast } from "sonner";
import ResultsEditor from "./ResultsEditor";

declare module "jspdf" {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
    lastAutoTable?: { finalY: number };
  }
}

interface ReportPreviewProps {
  test: any;
  open: boolean;
  onClose: () => void;
  onTestUpdate?: (updatedTest: any) => void;
}

// Get performance levels from settings
const getPerformanceLevels = () => {
  const saved = localStorage.getItem("performanceLevels");
  if (saved) {
    const levels = JSON.parse(saved);
    return {
      excellent: { min: levels.excellent.min, label: "ممتاز", color: [34, 197, 94] },
      good: { min: levels.good.min, max: levels.excellent.min - 1, label: "جيد", color: [59, 130, 246] },
      average: { min: levels.average.min, max: levels.good.min - 1, label: "متوسط", color: [245, 158, 11] },
      low: { min: levels.low.min, max: levels.average.min - 1, label: "متدني", color: [239, 68, 68] },
      failed: { max: levels.low.min - 1, label: "راسب", color: [220, 38, 38] }
    };
  }
  return {
    excellent: { min: 85, label: "ممتاز", color: [34, 197, 94] },
    good: { min: 75, max: 84, label: "جيد", color: [59, 130, 246] },
    average: { min: 65, max: 74, label: "متوسط", color: [245, 158, 11] },
    low: { min: 50, max: 64, label: "متدني", color: [239, 68, 68] },
    failed: { max: 49, label: "راسب", color: [220, 38, 38] }
  };
};

// Get header settings
const getHeaderSettings = () => {
  const saved = localStorage.getItem("headerSettings");
  if (saved) {
    return JSON.parse(saved);
  }
  return {
    rightLine1: "دولة فلسطين",
    rightLine1En: "State of Palestine",
    rightLine2: "وزارة التربية والتعليم العالي",
    rightLine3: "مديرية التربية والتعليم",
    leftLine1: "Ministry of Education",
    leftLine2: "Directorate of Education",
    leftLine3: "",
  };
};

const ReportPreview: React.FC<ReportPreviewProps> = ({ test, open, onClose, onTestUpdate }) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentTest, setCurrentTest] = useState(test);
  const pdfDocRef = useRef<jsPDF | null>(null);

  const getSchoolInfo = () => {
    return {
      schoolName: localStorage.getItem("schoolName") || "المدرسة",
      directorName: localStorage.getItem("directorName") || "المدير",
      schoolLogo: localStorage.getItem("schoolLogo") || "",
      ministryName: localStorage.getItem("ministryName") || "وزارة التربية والتعليم العالي",
      directorateName: localStorage.getItem("directorateName") || "مديرية التربية والتعليم",
    };
  };

  const getStudentName = (studentId: string, studentNameFromResult?: string, studentNameLookup?: Map<string, string>): string => {
    if (studentNameFromResult && studentNameFromResult !== studentId) return studentNameFromResult;
    const fromLookup = studentNameLookup?.get(studentId);
    if (fromLookup) return fromLookup;
    const student = getStudentById(studentId);
    return student?.name || "طالب غير معروف";
  };

  const getTestDetails = (lookups?: {
    classById?: Map<string, string>;
    sectionById?: Map<string, string>;
    subjectById?: Map<string, string>;
    teacherById?: Map<string, string>;
  }) => {
    const classNameFromLookup = lookups?.classById?.get(currentTest.classId);
    const sectionNameFromLookup = lookups?.sectionById?.get(currentTest.sectionId);
    const subjectNameFromLookup = lookups?.subjectById?.get(currentTest.subjectId);
    const teacherNameFromLookup = lookups?.teacherById?.get(currentTest.teacherId);

    const classInfo = !classNameFromLookup ? getClassById(currentTest.classId) : undefined;
    const sectionInfo = !sectionNameFromLookup ? getSectionById(currentTest.classId, currentTest.sectionId) : undefined;
    const subjectInfo = !subjectNameFromLookup ? getSubjectById(currentTest.subjectId) : undefined;
    const teacherInfo = !teacherNameFromLookup ? getTeacherById(currentTest.teacherId) : undefined;

    return {
      className: classNameFromLookup || classInfo?.name || currentTest.className || "غير محدد",
      sectionName: sectionNameFromLookup || sectionInfo?.name || currentTest.sectionName || "غير محدد",
      subjectName: subjectNameFromLookup || subjectInfo?.name || currentTest.subjectName || "غير محدد",
      teacherName: teacherNameFromLookup || teacherInfo?.name || currentTest.teacherName || "غير محدد",
    };
  };

  const getTestTypeLabel = (type: string): string => {
    const types: Record<string, string> = {
      exam: "امتحان رسمي",
      quiz: "اختبار قصير",
      homework: "واجب منزلي",
      worksheet: "ورقة عمل",
      manual: "إدخال يدوي"
    };
    return types[type] || type;
  };

  const calculateStats = () => {
    const levels = getPerformanceLevels();
    const presentStudents = currentTest.results.filter((r: any) => !r.isAbsent);
    const totalMaxScore = currentTest.questions.reduce((sum: number, q: any) => sum + q.maxScore, 0);
    
    // Dynamic performance levels calculation
    const excellentStudents = presentStudents.filter((r: any) => r.percentage >= levels.excellent.min);
    const goodStudents = presentStudents.filter((r: any) => r.percentage >= levels.good.min && r.percentage < levels.excellent.min);
    const averageStudents = presentStudents.filter((r: any) => r.percentage >= levels.average.min && r.percentage < levels.good.min);
    const lowStudents = presentStudents.filter((r: any) => r.percentage >= levels.low.min && r.percentage < levels.average.min);
    const failedStudents = presentStudents.filter((r: any) => r.percentage < levels.low.min);
    const absentStudents = currentTest.results.filter((r: any) => r.isAbsent);

    const scores = presentStudents.map((r: any) => r.percentage);
    const totalScores = presentStudents.map((r: any) => r.totalScore);
    const highestScore = scores.length > 0 ? Math.max(...scores) : 0;
    const lowestScore = scores.length > 0 ? Math.min(...scores) : 0;
    const highestTotal = totalScores.length > 0 ? Math.max(...totalScores) : 0;
    const lowestTotal = totalScores.length > 0 ? Math.min(...totalScores) : 0;
    const avgScore = scores.length > 0 ? scores.reduce((sum: number, s: number) => sum + s, 0) / scores.length : 0;
    const avgTotal = totalScores.length > 0 ? totalScores.reduce((sum: number, s: number) => sum + s, 0) / totalScores.length : 0;
    const passRate = presentStudents.length > 0 ? ((presentStudents.length - failedStudents.length) / presentStudents.length) * 100 : 0;

    // Calculate stats for each question
    const questionStats = currentTest.questions.map((question: any, idx: number) => {
      const questionScores: number[] = [];
      
      presentStudents.forEach((result: any) => {
        const score = result.scores[question.id] || 0;
        const percentage = (score / question.maxScore) * 100;
        questionScores.push(percentage);
      });

      const excellentCount = questionScores.filter(s => s >= 85).length;
      const goodCount = questionScores.filter(s => s >= 75 && s < 85).length;
      const averageCount = questionScores.filter(s => s >= 65 && s < 75).length;
      const lowCount = questionScores.filter(s => s >= 50 && s < 65).length;
      const failedCount = questionScores.filter(s => s < 50).length;
      
      const rawScores = presentStudents.map((r: any) => r.scores[question.id] || 0);
      const questionTotal = rawScores.reduce((a: number, b: number) => a + b, 0);
      const questionAvg = rawScores.length > 0 ? questionTotal / rawScores.length : 0;
      const questionHighest = rawScores.length > 0 ? Math.max(...rawScores) : 0;
      const questionLowest = rawScores.length > 0 ? Math.min(...rawScores) : 0;
      const questionPassRate = questionScores.length > 0 
        ? (questionScores.filter(s => s >= 50).length / questionScores.length) * 100 
        : 0;

      return {
        questionNum: idx + 1,
        type: question.type,
        maxScore: question.maxScore,
        excellentCount,
        goodCount,
        averageCount,
        lowCount,
        failedCount,
        avgScore: questionAvg,
        highestScore: questionHighest,
        lowestScore: questionLowest,
        passRate: questionPassRate,
      };
    });

    return {
      totalStudents: currentTest.results.length,
      presentCount: presentStudents.length,
      absentCount: absentStudents.length,
      excellentCount: excellentStudents.length,
      goodCount: goodStudents.length,
      averageCount: averageStudents.length,
      lowCount: lowStudents.length,
      failedCount: failedStudents.length,
      passedCount: presentStudents.length - failedStudents.length,
      highestScore,
      lowestScore,
      highestTotal,
      lowestTotal,
      avgScore,
      avgTotal,
      passRate,
      questionStats,
      totalMaxScore,
    };
  };

  const generatePDF = async (): Promise<jsPDF> => {
    const { schoolName, directorName, ministryName, directorateName } = getSchoolInfo();

    // Load lookups from backend (DB) to ensure names are available even when localStorage dataService is empty
    let lookups: {
      classById: Map<string, string>;
      sectionById: Map<string, string>;
      subjectById: Map<string, string>;
      teacherById: Map<string, string>;
      studentById: Map<string, string>;
    } | null = null;

    try {
      const [classes, sections, subjects, teachers, students] = await Promise.all([
        getClassesDB(),
        getSectionsDB(),
        getSubjectsDB(),
        getTeachersDB(),
        getStudentsDB(),
      ]);

      lookups = {
        classById: new Map((classes || []).map((c: any) => [c.id, c.name])),
        sectionById: new Map((sections || []).map((s: any) => [s.id, s.name])),
        subjectById: new Map((subjects || []).map((s: any) => [s.id, s.name])),
        teacherById: new Map((teachers || []).map((t: any) => [t.id, t.name])),
        studentById: new Map((students || []).map((st: any) => [st.id, st.name])),
      };
    } catch (e) {
      // Silent fallback to localStorage-based lookups
      lookups = null;
    }

    const { className, sectionName, subjectName, teacherName } = getTestDetails(
      lookups
        ? {
            classById: lookups.classById,
            sectionById: lookups.sectionById,
            subjectById: lookups.subjectById,
            teacherById: lookups.teacherById,
          }
        : undefined
    );

    const stats = calculateStats();

    // Use portrait orientation for vertical layout
    const doc = new jsPDF({ 
      orientation: "portrait", 
      unit: "mm", 
      format: "a4" 
    });

    // Load Arabic font
    try {
      const fontBase64 = await loadAmiriFont();
      doc.addFileToVFS("Amiri-Regular.ttf", fontBase64);
      doc.addFont("Amiri-Regular.ttf", ARABIC_FONT_NAME, "normal");
      doc.setFont(ARABIC_FONT_NAME);
    } catch (error) {
      console.error("Failed to load Arabic font:", error);
      doc.setFont("helvetica");
    }

    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 6;

    // === HEADER SECTION - Unified with Palestine Logo Only ===
    let currentY = 6;

    // Classic double-line frame border at top
    doc.setDrawColor(80, 80, 80);
    doc.setLineWidth(0.6);
    doc.rect(margin, 4, pageWidth - margin * 2, 22);
    doc.setLineWidth(0.2);
    doc.rect(margin + 0.8, 4.8, pageWidth - margin * 2 - 1.6, 20.4);

    currentY = 8;

    // Get header settings
    const headerSettings = getHeaderSettings();

    // Three-column header - more compact
    const leftCol = pageWidth - margin - 35;
    const centerCol = pageWidth / 2;
    const rightCol = margin + 35;

    // Right side - Ministry info (Arabic)
    doc.setFontSize(7);
    doc.setTextColor(0, 0, 0);
    doc.text(headerSettings.rightLine1 || "دولة فلسطين", rightCol, currentY, { align: "center" });
    doc.setFontSize(6);
    doc.text(headerSettings.rightLine1En || "State of Palestine", rightCol, currentY + 3, { align: "center" });
    doc.setFontSize(7);
    doc.text(headerSettings.rightLine2 || ministryName, rightCol, currentY + 7, { align: "center" });
    doc.text(headerSettings.rightLine3 || directorateName, rightCol, currentY + 11, { align: "center" });

    // Center - Header Logo from settings (for reports only)
    const headerLogo = headerSettings.headerLogo || "";
    if (headerLogo) {
      try {
        doc.addImage(headerLogo, "PNG", centerCol - 6, currentY, 12, 12);
      } catch (e) {
        // Fallback if image fails
        doc.setFillColor(0, 100, 0);
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.3);
        doc.circle(centerCol, currentY + 5, 6, "S");
        doc.setFontSize(5);
        doc.text("🇵🇸", centerCol, currentY + 6, { align: "center" });
      }
    } else {
      // Default emblem placeholder
      doc.setFillColor(0, 100, 0);
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.3);
      doc.circle(centerCol, currentY + 5, 6, "S");
      doc.setFontSize(5);
      doc.text("🇵🇸", centerCol, currentY + 6, { align: "center" });
    }
    
    // School name below emblem
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    doc.text(schoolName, centerCol, currentY + 14, { align: "center" });

    // Left side - English info
    doc.setFontSize(7);
    doc.text(headerSettings.leftLine1 || "Ministry of Education", leftCol, currentY + 2, { align: "center" });
    doc.text(headerSettings.leftLine2 || "Directorate of Education", leftCol, currentY + 6, { align: "center" });
    if (headerSettings.leftLine3) {
      doc.text(headerSettings.leftLine3, leftCol, currentY + 10, { align: "center" });
    }

    currentY = 28;

    // Report Title with test type
    const testTypeLabel = currentTest.type ? ` (${getTestTypeLabel(currentTest.type)})` : "";
    doc.setFontSize(10);
    doc.setTextColor(0, 100, 0);
    doc.text(`تحليل امتحان ${currentTest.name}${testTypeLabel}`, centerCol, currentY, { align: "center" });
    
    currentY += 4;

    // === TEST INFO ROW - Compact with Teacher Name ===
    doc.autoTable({
      startY: currentY,
      head: [["علامة الاختبار", "معلم المادة", "التاريخ", "الصف / الشعبة", "المبحث"]],
      body: [[`${stats.totalMaxScore} درجة`, teacherName, currentTest.date, `${className} / ${sectionName}`, subjectName]],
      theme: "grid",
      styles: { 
        halign: "center", 
        fontSize: 7, 
        cellPadding: 1, 
        font: ARABIC_FONT_NAME 
      },
      headStyles: { 
        fillColor: [0, 100, 0], 
        textColor: [255, 255, 255], 
        fontSize: 7 
      },
      margin: { left: margin, right: margin },
    });

    currentY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 2 : currentY + 12;

    // === MAIN RESULTS TABLE - Optimized for portrait A4 ===
    const questionHeaders = currentTest.questions.map((q: any) => q.type);
    const mainHeaders = ["ملاحظات", "النسبة", "المجموع", ...questionHeaders.reverse(), "اسم الطالب", "م"];

    const mainTableData = currentTest.results.map((result: any, index: number) => {
      const sid = result.studentId || result.student_id;
      const studentName = getStudentName(
        sid,
        result.studentName,
        lookups?.studentById
      );
      if (result.isAbsent) {
        const emptyScores = currentTest.questions.map(() => "-");
        return ["غائب", "-", "-", ...emptyScores.reverse(), studentName, index + 1];
      }

      const questionScores = currentTest.questions.map((q: any) => result.scores[q.id] || 0);
      let status = "";
      if (result.percentage < 50) status = "راسب";
      else if (result.percentage < 65) status = "متدني";
      else if (result.percentage < 75) status = "متوسط";
      else if (result.percentage < 85) status = "جيد";
      else status = "ممتاز";
      
      return [
        status,
        `${result.percentage}%`,
        result.totalScore,
        ...questionScores.reverse(),
        studentName,
        index + 1
      ];
    });

    // Calculate dynamic font size based on number of students - smaller for portrait
    const studentCount = currentTest.results.length;
    const fontSize = studentCount > 35 ? 5 : studentCount > 25 ? 6 : studentCount > 15 ? 7 : 8;
    const cellPadding = studentCount > 35 ? 0.5 : studentCount > 25 ? 0.7 : 0.8;

    doc.autoTable({
      startY: currentY,
      head: [mainHeaders],
      body: mainTableData,
      theme: "grid",
      styles: { 
        halign: "center", 
        fontSize: fontSize, 
        cellPadding: cellPadding, 
        font: ARABIC_FONT_NAME,
        lineWidth: 0.08,
      },
      headStyles: { 
        fillColor: [0, 100, 0], 
        textColor: [255, 255, 255], 
        fontSize: fontSize,
        cellPadding: 1,
      },
      columnStyles: {
        0: { cellWidth: 12 },
        1: { cellWidth: 10 },
        2: { cellWidth: 10 },
      },
      margin: { left: margin, right: margin },
      didParseCell: (data: any) => {
        if (data.section === "body" && data.column.index === 0) {
          const text = data.cell.raw;
          if (text === "راسب") {
            data.cell.styles.fillColor = [255, 200, 200];
            data.cell.styles.textColor = [150, 0, 0];
          } else if (text === "متدني") {
            data.cell.styles.fillColor = [255, 230, 200];
            data.cell.styles.textColor = [180, 80, 0];
          } else if (text === "متوسط") {
            data.cell.styles.fillColor = [255, 255, 200];
            data.cell.styles.textColor = [150, 120, 0];
          } else if (text === "جيد") {
            data.cell.styles.fillColor = [200, 220, 255];
            data.cell.styles.textColor = [0, 60, 150];
          } else if (text === "ممتاز") {
            data.cell.styles.fillColor = [200, 255, 200];
            data.cell.styles.textColor = [0, 100, 0];
          } else if (text === "غائب") {
            data.cell.styles.fillColor = [240, 240, 240];
            data.cell.styles.textColor = [100, 100, 100];
          }
        }
      },
    });

    currentY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 3 : currentY + 50;

    // === COMPACT STATISTICS SECTION - Always at Bottom ===
    // Check if we need a new page - only if very close to bottom
    if (currentY > pageHeight - 40) {
      doc.addPage();
      currentY = 10;
    }

    // Performance levels summary table - compact horizontal layout
    const statsTableWidth = pageWidth - margin * 2;
    
    doc.autoTable({
      startY: currentY,
      head: [["راسب (<50%)", "متدني (50-64%)", "متوسط (65-74%)", "جيد (75-84%)", "ممتاز (85%+)", "المستوى"]],
      body: [[
        `${stats.failedCount} (${((stats.failedCount / stats.presentCount) * 100 || 0).toFixed(0)}%)`,
        `${stats.lowCount} (${((stats.lowCount / stats.presentCount) * 100 || 0).toFixed(0)}%)`,
        `${stats.averageCount} (${((stats.averageCount / stats.presentCount) * 100 || 0).toFixed(0)}%)`,
        `${stats.goodCount} (${((stats.goodCount / stats.presentCount) * 100 || 0).toFixed(0)}%)`,
        `${stats.excellentCount} (${((stats.excellentCount / stats.presentCount) * 100 || 0).toFixed(0)}%)`,
        "العدد (النسبة)"
      ]],
      theme: "grid",
      styles: { 
        halign: "center", 
        fontSize: 6, 
        cellPadding: 0.8, 
        font: ARABIC_FONT_NAME 
      },
      headStyles: { 
        fillColor: [50, 50, 50], 
        textColor: [255, 255, 255], 
        fontSize: 6 
      },
      margin: { left: margin, right: margin },
      didParseCell: (data: any) => {
        if (data.section === "body") {
          const colIndex = data.column.index;
          if (colIndex === 4) data.cell.styles.fillColor = [200, 255, 200];
          else if (colIndex === 3) data.cell.styles.fillColor = [200, 220, 255];
          else if (colIndex === 2) data.cell.styles.fillColor = [255, 255, 200];
          else if (colIndex === 1) data.cell.styles.fillColor = [255, 230, 200];
          else if (colIndex === 0) data.cell.styles.fillColor = [255, 200, 200];
        }
      },
    });

    currentY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 2 : currentY + 15;

    // Removed detailed question statistics table

    // === SUMMARY BOX - Compact ===
    if (currentY > pageHeight - 20) {
      doc.addPage();
      currentY = 10;
    }

    doc.autoTable({
      startY: currentY,
      head: [["نسبة النجاح", "راسب", "ناجح", "غائب", "حاضر", "المجموع"]],
      body: [[
        `${stats.passRate.toFixed(1)}%`,
        stats.failedCount,
        stats.passedCount,
        stats.absentCount,
        stats.presentCount,
        stats.totalStudents
      ]],
      theme: "grid",
      styles: { 
        halign: "center", 
        fontSize: 6, 
        cellPadding: 0.8, 
        font: ARABIC_FONT_NAME 
      },
      headStyles: { 
        fillColor: [100, 100, 100], 
        textColor: [255, 255, 255], 
        fontSize: 6 
      },
      margin: { left: margin, right: margin },
    });

    // === FOOTER ON EACH PAGE - Compact ===
    const pageCount = doc.internal.pages.length - 1;
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      
      doc.setDrawColor(0, 100, 0);
      doc.setLineWidth(0.3);
      doc.line(margin, pageHeight - 10, pageWidth - margin, pageHeight - 10);

      doc.setFontSize(7);
      doc.setTextColor(80, 80, 80);
      doc.text(`مدير/ة المدرسة: ${directorName}`, pageWidth - margin, pageHeight - 6, { align: "right" });
      doc.text(`نسبة النجاح: ${stats.passRate.toFixed(1)}%`, centerCol, pageHeight - 6, { align: "center" });
      doc.text(`صفحة ${i} من ${pageCount}`, margin, pageHeight - 6);

      // Palestinian flag bar at bottom - smaller
      doc.setFillColor(0, 0, 0);
      doc.rect(0, pageHeight - 3, pageWidth, 1, "F");
      doc.setFillColor(255, 255, 255);
      doc.rect(0, pageHeight - 2, pageWidth, 1, "F");
      doc.setFillColor(0, 128, 0);
      doc.rect(0, pageHeight - 1, pageWidth, 1, "F");
    }

    return doc;
  };

  // Update currentTest when test prop changes
  useEffect(() => {
    if (test) {
      setCurrentTest(test);
    }
  }, [test]);

  const regeneratePDF = useCallback(async () => {
    if (!currentTest) return;
    
    setIsLoading(true);
    try {
      const doc = await generatePDF();
      pdfDocRef.current = doc;
      
      // Revoke old URL
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
      
      const blob = doc.output("blob");
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      toast.success("تم تحديث التقرير");
    } catch (error) {
      console.error("Error regenerating PDF:", error);
      toast.error("حدث خطأ في تحديث التقرير");
    } finally {
      setIsLoading(false);
    }
  }, [currentTest, pdfUrl]);

  const handleSaveResults = useCallback(async (updatedTest: any) => {
    setCurrentTest(updatedTest);
    setIsEditMode(false);
    
    // Notify parent of update
    if (onTestUpdate) {
      onTestUpdate(updatedTest);
    }
    
    // Wait a bit for state to update, then regenerate
    setTimeout(async () => {
      await regeneratePDF();
    }, 100);
  }, [onTestUpdate, regeneratePDF]);

  useEffect(() => {
    if (open && currentTest) {
      setIsLoading(true);
      generatePDF()
        .then((doc) => {
          pdfDocRef.current = doc;
          const blob = doc.output("blob");
          const url = URL.createObjectURL(blob);
          setPdfUrl(url);
        })
        .catch((error) => {
          console.error("Error generating PDF:", error);
          toast.error("حدث خطأ في إنشاء التقرير");
        })
        .finally(() => {
          setIsLoading(false);
        });
    }

    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [open, currentTest]);

  const handlePrint = () => {
    if (pdfUrl) {
      const printWindow = window.open(pdfUrl, "_blank");
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    }
  };

  const handleDownload = () => {
    if (pdfDocRef.current) {
      pdfDocRef.current.save(`تقرير_${currentTest.name}_${currentTest.date}.pdf`);
      toast.success("تم تحميل التقرير بنجاح");
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent className="max-w-6xl h-[95vh] flex flex-col p-0 gap-0">
          <DialogHeader className="p-4 border-b bg-gradient-to-r from-[#000000] via-[#008000] to-[#CE1126]">
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText className="h-5 w-5" />
              معاينة التقرير - {currentTest?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center space-y-4">
                  <Loader2 className="h-12 w-12 animate-spin mx-auto text-[#008000]" />
                  <p className="text-muted-foreground">جاري إنشاء التقرير...</p>
                </div>
              </div>
            ) : pdfUrl ? (
              <iframe
                src={pdfUrl}
                className="w-full h-full"
                title="معاينة التقرير"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-muted-foreground">لا يمكن عرض التقرير</p>
              </div>
            )}
          </div>

          <div className="p-4 border-t flex justify-between items-center bg-muted/30">
            <Button variant="outline" onClick={onClose}>
              <X className="ml-2 h-4 w-4" />
              إغلاق
            </Button>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setIsEditMode(true)} 
                disabled={!pdfUrl || isLoading}
                className="border-amber-500 text-amber-600 hover:bg-amber-50"
              >
                <Edit className="ml-2 h-4 w-4" />
                تعديل النتائج
              </Button>
              <Button onClick={handlePrint} disabled={!pdfUrl}>
                <Printer className="ml-2 h-4 w-4" />
                طباعة
              </Button>
              <Button onClick={handleDownload} disabled={!pdfUrl} className="bg-[#008000] hover:bg-[#006000]">
                <Download className="ml-2 h-4 w-4" />
                تحميل PDF
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Results Editor Dialog */}
      <ResultsEditor
        test={currentTest}
        open={isEditMode}
        onClose={() => setIsEditMode(false)}
        onSave={handleSaveResults}
      />
    </>
  );
};

export default ReportPreview;
